import type { CancelablePromise } from '@/api/client'
import { ApiError } from '@/api/client'
import { useAuthStore } from '@/stores/AuthStore'

/**
 * This functions helps us handle all the default error cases that can occur when making a request including timeouts
 * @param {CancelablePromise} promise
 * @param {number} timeout in milliseconds
 * @returns The response data or undefined if an error occurred. The error is logged to the console.
 */
export default async function <T>(
  promise: CancelablePromise<T>,
  timeout = 60000,
): Promise<T | undefined> {
  try {
    setTimeout(() => {
      promise.cancel()
      if (promise.isCancelled) {
        console.error(
          `handleRequest: A request was cancelled because it timed out after ${timeout}ms`,
        )
      }
    }, timeout)
    return await promise
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 401) {
        console.error(error)
        await useAuthStore().logout()
        return
      } else if (error.status === 422) {
        console.error(error)
        return
      }
    } else {
      throw error
    }
  }
}
